import { create } from 'zustand';
import { useEffect } from 'react';
import { X } from 'lucide-react';

// ── Store ─────────────────────────────────────────────────────────────────────

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastStore {
  toasts: Toast[];
  add: (type: ToastType, message: string, duration?: number) => void;
  remove: (id: string) => void;
}

let _counter = 0;

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  add: (type, message, duration = 4000) => {
    const id = `toast-${++_counter}`;
    set((s) => ({ toasts: [...s.toasts, { id, type, message, duration }] }));
    if (duration > 0) {
      setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), duration);
    }
  },
  remove: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

// Convenience functions
export const toast = {
  success: (msg: string, ms?: number) => useToastStore.getState().add('success', msg, ms),
  error:   (msg: string, ms?: number) => useToastStore.getState().add('error', msg, ms ?? 6000),
  info:    (msg: string, ms?: number) => useToastStore.getState().add('info', msg, ms),
  warning: (msg: string, ms?: number) => useToastStore.getState().add('warning', msg, ms ?? 5000),
};

// ── Renderer ──────────────────────────────────────────────────────────────────

// Design system Toast: card ground, hairline, a 3px top edge in the status
// colour and the toast shadow. The message is the one line saying what
// happened; errors are announced as alerts, everything else as status.
const EDGE: Record<ToastType, string> = {
  success: 'ap-toast--success',
  error:   'ap-toast--danger',
  info:    'ap-toast--info',
  warning: 'ap-toast--warning',
};

function ToastItem({ toast: t, onRemove }: { toast: Toast; onRemove: () => void }) {
  useEffect(() => {
    const timer = t.duration && t.duration > 0
      ? setTimeout(onRemove, t.duration)
      : undefined;
    return () => timer && clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`ap-toast ${EDGE[t.type]}`} role={t.type === 'error' ? 'alert' : 'status'}>
      <b>{t.message}</b>
      <button type="button" onClick={onRemove} aria-label="Dismiss">
        <X className="mx-auto h-4 w-4" />
      </button>
    </div>
  );
}

export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);
  const remove = useToastStore((s) => s.remove);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[100] flex w-[380px] max-w-[calc(100vw-2rem)] flex-col gap-3">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onRemove={() => remove(t.id)} />
      ))}
    </div>
  );
}
